import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { downgradeInjectable } from '@angular/upgrade/static';
import * as angular from 'angular';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { AppState } from '../../store';
import { selectAccountId } from '../../account/store/account.selectors';
import { Job, JobLog, JobOutput, JobOutputPreview } from '../jobs.models';
import { QueryParams } from '../../models/http.models';

@Injectable({
  providedIn: 'root',
})
export class JobsResource {
  private apiRoot: string = `${process.env.API_URL}`;

  constructor(private http: HttpClient, private store: Store<AppState>) {}

  get(job_id: number, params?: QueryParams): Observable<Job> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) => this.http.get<Job>(`${this.apiRoot}/${account_id}/api/jobs/${job_id}`, { params })),
    );
  }

  save(body: Partial<Job>, params?: QueryParams): Observable<Job> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) => this.http.post<Job>(`${this.apiRoot}/${account_id}/api/jobs`, body, { params })),
    );
  }

  update(job_id: number, body: Partial<Job>, params?: QueryParams): Observable<Job> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.put<Job>(`${this.apiRoot}/${account_id}/api/jobs/${job_id}`, body, { params }),
      ),
    );
  }

  remove(job_id: number, params?: QueryParams): Observable<any> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) => this.http.delete<Job>(`${this.apiRoot}/${account_id}/api/jobs/${job_id}`, { params })),
    );
  }

  query(params?: QueryParams): Observable<Job[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) => this.http.get<Job[]>(`${this.apiRoot}/${account_id}/api/jobs`, { params })),
    );
  }

  search(params?: QueryParams): Observable<Job[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) => this.http.get<Job[]>(`${this.apiRoot}/${account_id}/api/jobs/search`, { params })),
    );
  }

  children(job_id: number, params?: QueryParams): Observable<Job[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<Job[]>(`${this.apiRoot}/${account_id}/api/jobs/${job_id}/children`, { params }),
      ),
    );
  }

  log(job_id: number, params?: QueryParams): Observable<JobLog> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<JobLog>(`${this.apiRoot}/${account_id}/api/jobs/${job_id}/log`, { params }),
      ),
    );
  }

  variables(job_id: number, params?: QueryParams): Observable<Job> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<Job>(`${this.apiRoot}/${account_id}/api/jobs/${job_id}/variables`, { params }),
      ),
    );
  }

  output(output_id: number, job_id: number, params?: QueryParams): Observable<JobOutputPreview> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<JobOutputPreview>(
          `${this.apiRoot}/${account_id}/api/jobs/${job_id}/outputs/${output_id}/preview`,
          { params },
        ),
      ),
    );
  }
}

((window as any).xplenty as angular.IModule).service('Job', downgradeInjectable(JobsResource));
